import { createContext, useContext } from "react";

/**
 * WS 샘플 화면 데이터 — 월성복지관 2026 청년도전지원사업 실데이터(ws_youth_daily_data.csv) 기반
 * - CSV는 개인정보가 포함되어 저장소에 커밋하지 않고(public/ws_sample/*.csv는 .gitignore), 실행 시 fetch로 불러옵니다.
 * - 기존 샘플(src/sample)의 가상 데이터 생성 함수와 같은 필드명으로 집계해, 화면 컴포넌트는 그대로 재사용합니다.
 * - 조회 기준일("오늘")은 실제 오늘이 아니라 데이터의 마지막 날짜입니다. (데이터가 끝난 뒤 기간은 비어 보이므로)
 */

export const WS_CSV_URL = `${process.env.PUBLIC_URL}/ws_sample/ws_youth_daily_data.csv`;

export const WS_ORGANIZATION = "월성복지관";
export const WS_PROGRAM_NAME = "2026 청년도전지원사업";
export const WS_PROGRAM_CODE = "WS-YOUTH-2026";

// 디스트레스 4단계 경계 (개인 상세 DISTRESS_LEVELS, 종합현황 "기관 평균 마음 상태"와 같은 구간)
const DISTRESS_STABLE_MAX = 0.16;
const DISTRESS_CAUTION_MAX = 0.41;
const DISTRESS_WARNING_MAX = 0.6;
// 참가자 목록 "현재 상태"를 판단할 때 쓰는 최근 디스트레스 기록 개수
const STATUS_RECENT_RECORDS = 7;
// 이 기간(일) 안에 활동(대화·감정일기·디스트레스 기록)이 있으면 "활발"
const ACTIVE_WINDOW_DAYS = 7;

// ─── 날짜 유틸 ─────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, "0");
export const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
export const parseDate = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};
export const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};
export const getDateList = (start, end) => {
  const dates = [];
  for (let d = parseDate(start); d <= parseDate(end); d = addDays(d, 1)) dates.push(toDateString(d));
  return dates;
};
const shiftDate = (dateStr, days) => toDateString(addDays(parseDate(dateStr), days));

// 데이터 로드 후 마지막 날짜로 설정 — 로드 전에는 실제 오늘 날짜
let referenceDate = null;
export const getReferenceDate = () => referenceDate ?? toDateString(new Date());

/** 기준일(데이터 마지막 날짜)을 포함한 최근 n일 범위 */
export const getQuickRange = (days, end = getReferenceDate()) => ({ start: shiftDate(end, -(days - 1)), end });

const round2 = (value) => Math.round(value * 100) / 100;
const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);

export const getDistressBand = (value) => {
  if (value <= DISTRESS_STABLE_MAX) return "stable";
  if (value <= DISTRESS_CAUTION_MAX) return "caution";
  if (value <= DISTRESS_WARNING_MAX) return "warning";
  return "high";
};

// ─── CSV 파싱 ──────────────────────────────────────────────────────
/** 따옴표 안의 쉼표·줄바꿈을 지원하는 CSV 파서 */
const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i];
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (c === '"') quoted = false;
      else field += c;
    } else if (c === '"') quoted = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += c;
  }
  if (field || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell !== ""));
};

const toNumber = (cell) => (cell === "" || cell == null ? null : Number(cell));
// 같은 날 여러 번 검사하면 "3, 0"처럼 쉼표로 이어져 있음
const toScores = (cell) =>
  (cell ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter(Number.isFinite);

const getAge = (birth, onDate) => {
  if (!birth) return null;
  const [by, bm, bd] = birth.split("-").map(Number);
  const [y, m, d] = onDate.split("-").map(Number);
  return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
};

const GENDER = { 남: "M", 여: "F" };

/** 테스트 결과 배열 → 회차 이력 (최근 회차가 배열의 첫 번째, 기존 phq_history 형태) */
const toHistory = (tests) =>
  tests
    .map((t, i) => ({ attempt: i + 1, completed_at: t.date, total_score: t.score }))
    .reverse();

/** 하루 기록이 활동(대화·감정일기·디스트레스 산출)으로 볼 수 있는지 */
const isActiveRecord = (rec) => Boolean(rec) && (rec.messages > 0 || rec.diaries > 0 || typeof rec.distress === "number");

/**
 * CSV 원문 → 화면용 데이터셋
 * @returns {{ participants: Array, endDate: string, projects: Array }}
 */
export const buildDataset = (text) => {
  const [header, ...rows] = parseCsv(text.replace(/^﻿/, ""));
  const col = Object.fromEntries(header.map((name, i) => [name.trim(), i]));
  const get = (row, name) => row[col[name]] ?? "";

  const byUser = new Map();
  let endDate = "";

  rows.forEach((row) => {
    const userId = get(row, "사용자 ID");
    const date = get(row, "날짜");
    if (!userId || !date) return;
    if (date > endDate) endDate = date;

    let user = byUser.get(userId);
    if (!user) {
      user = {
        user_id: userId,
        name: get(row, "사용자 이름") || "(이름 없음)",
        gender: GENDER[get(row, "성별")] ?? null,
        birth: get(row, "생년월일"),
        code: get(row, "초대코드"),
        joined_at: get(row, "가입일시").slice(0, 10),
        daily: new Map(),
        phqTests: [],
        gadTests: [],
      };
      byUser.set(userId, user);
    }

    toScores(get(row, "PHQ 점수")).forEach((score) => user.phqTests.push({ date, score }));
    toScores(get(row, "GAD 점수")).forEach((score) => user.gadTests.push({ date, score }));

    user.daily.set(date, {
      date,
      messages: toNumber(get(row, "메시지 수")) ?? 0,
      diaries: toNumber(get(row, "감정일기 수")) ?? 0,
      distress: toNumber(get(row, "고통지수")),
      emo: toNumber(get(row, "emo_score")),
      sym: toNumber(get(row, "sym_score")),
      baseline: toNumber(get(row, "baseline_score")),
      isEpisode: get(row, "is_episode") === "Y",
      episodeStart: get(row, "episode_start_trigger") === "Y",
    });
  });

  referenceDate = endDate || null;

  const participants = [...byUser.values()].map((user) => {
    const records = [...user.daily.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
    user.phqTests.sort((a, b) => (a.date < b.date ? -1 : 1));
    user.gadTests.sort((a, b) => (a.date < b.date ? -1 : 1));
    const lastActive = [...records].reverse().find(isActiveRecord)?.date ?? null;
    return {
      ...user,
      records,
      age: getAge(user.birth, endDate),
      lastActiveDate: lastActive,
      activity: lastActive && lastActive > shiftDate(endDate, -ACTIVE_WINDOW_DAYS) ? "active" : "inactive",
      status: getCurrentStatus(records),
      phq_history: toHistory(user.phqTests),
      gad_history: toHistory(user.gadTests),
    };
  });

  const codes = [...new Set(participants.map((p) => p.code).filter(Boolean))].sort();
  const projects = codes.map((code) => ({
    id: code,
    organization: WS_ORGANIZATION,
    name: `청년도전지원사업 ${code.replace(`${WS_PROGRAM_CODE}-`, "")}`,
    code,
  }));

  return { participants, endDate, projects, orgDailyCache: new Map() };
};

/**
 * 참가자 목록의 "현재 상태" — 데이터 기준일까지의 마지막 디스트레스 기록 기준
 * - 마지막 기록이 에피소드 → 에피소드 / 최근 기록 평균이 고위험·주의 구간 → 고위험·주의 / 기록 없음 → none
 */
function getCurrentStatus(records) {
  const distressRecords = records.filter((r) => typeof r.distress === "number");
  if (distressRecords.length === 0) return "none";
  if (distressRecords[distressRecords.length - 1].isEpisode) return "episode";
  const recentAvg = mean(distressRecords.slice(-STATUS_RECENT_RECORDS).map((r) => r.distress));
  const band = getDistressBand(recentAvg);
  if (band === "high") return "high";
  if (band === "stable") return "stable";
  return "caution";
}

// ─── 조회 범위 ─────────────────────────────────────────────────────
/** 조회 범위("ALL" 또는 사업/초대코드)에 속한 참가자 — 사업 code는 초대코드 앞부분과 일치 */
export const getScopeParticipants = (dataset, scopeKey) =>
  scopeKey === "ALL" ? dataset.participants : dataset.participants.filter((p) => p.code.startsWith(scopeKey));

const recordsInRange = (participant, start, end) =>
  participant.records.filter((r) => r.date >= start && r.date <= end);

const testsUntil = (tests, end) => tests.filter((t) => t.date <= end);
const latestScoreUntil = (tests, end) => testsUntil(tests, end).at(-1)?.score ?? null;

/** 조회 종료일까지 가입한 참가자 */
const getMembers = (dataset, scopeKey, end) =>
  getScopeParticipants(dataset, scopeKey).filter((p) => !p.joined_at || p.joined_at <= end);

const getPeriodDistressAvg = (participant, start, end) =>
  mean(
    recordsInRange(participant, start, end)
      .map((r) => r.distress)
      .filter((v) => typeof v === "number"),
  );

// ─── 종합현황 ──────────────────────────────────────────────────────
/** 조회 기간의 종합현황 요약 (기존 buildOverviewSummary와 같은 필드명) */
export const buildOverviewSummary = (dataset, scopeKey, start, end) => {
  const members = getMembers(dataset, scopeKey, end);
  const periodRecords = members.flatMap((p) => recordsInRange(p, start, end));
  const distressAvgs = members.map((p) => getPeriodDistressAvg(p, start, end)).filter((v) => v !== null);
  const countTests = (key) => {
    const tests = members.flatMap((p) => p[key]);
    return {
      total: tests.filter((t) => t.date <= end).length,
      week_new: tests.filter((t) => t.date >= start && t.date <= end).length,
    };
  };
  const distressValues = periodRecords.map((r) => r.distress).filter((v) => typeof v === "number");

  return {
    org_total: members.length,
    org_respondents: members.filter((p) => recordsInRange(p, start, end).some(isActiveRecord)).length,
    // 디스트레스가 산출된 참가자 수 — 위험도 분포 도넛의 분모
    distress_respondents: distressAvgs.length,
    unexamined_count: members.filter(
      (p) => testsUntil(p.phqTests, end).length === 0 && testsUntil(p.gadTests, end).length === 0,
    ).length,
    phq: countTests("phqTests"),
    gad: countTests("gadTests"),
    // CSV에 자살·자해 위험 감지 정보가 없어 항상 0명
    suicide_risk_count: 0,
    episode_total: periodRecords.filter((r) => r.episodeStart).length,
    episode_user_count: members.filter((p) => recordsInRange(p, start, end).some((r) => r.isEpisode)).length,
    org_distress_avg: distressValues.length ? round2(mean(distressValues)) : null,
    distress_high_risk_count: distressAvgs.filter((v) => getDistressBand(v) === "high").length,
  };
};

/** 기간 평균 디스트레스 4단계별 인원 { stable, caution, warning, high } */
export const buildDistressDistribution = (dataset, scopeKey, start, end) => {
  const counts = { stable: 0, caution: 0, warning: 0, high: 0 };
  getMembers(dataset, scopeKey, end).forEach((p) => {
    const avg = getPeriodDistressAvg(p, start, end);
    if (avg !== null) counts[getDistressBand(avg)] += 1;
  });
  return counts;
};

/** 일별 활동 인원·에피소드 발생(시작) 건수 */
export const buildDailyActivitySeries = (dataset, scopeKey, start, end) => {
  const members = getMembers(dataset, scopeKey, end);
  return getDateList(start, end).map((date) => {
    const records = members.map((p) => p.daily.get(date)).filter(Boolean);
    return {
      date,
      participants: records.filter(isActiveRecord).length,
      episodes: records.filter((r) => r.episodeStart).length,
    };
  });
};

/**
 * 우선 확인 대상자 (기존 TriageTable 행 형태)
 * - episode: 기간 내 마지막 디스트레스 기록이 에피소드
 * - chronic: 기간 평균 디스트레스가 고위험 구간
 * - dropout: 기간 내 에피소드가 있었고 종료일 기준 최근 7일 활동 없음
 * - crisis: CSV에 위기 감지 정보가 없어 추출하지 않음
 */
export const buildTriageRows = (dataset, scopeKey, start, end) => {
  const last7 = Array.from({ length: 7 }, (_, i) => shiftDate(end, i - 6));

  return getMembers(dataset, scopeKey, end)
    .map((p) => {
      const periodRecords = recordsInRange(p, start, end);
      const distressRecords = periodRecords.filter((r) => typeof r.distress === "number");
      const periodAvg = mean(distressRecords.map((r) => r.distress));
      const recentActive = last7.some((date) => isActiveRecord(p.daily.get(date)));

      let level = null;
      if (distressRecords.at(-1)?.isEpisode) level = "episode";
      else if (periodAvg !== null && getDistressBand(periodAvg) === "high") level = "chronic";
      else if (periodRecords.some((r) => r.isEpisode) && !recentActive) level = "dropout";
      if (!level) return null;

      const trend = last7.map((date) => {
        const rec = p.daily.get(date);
        return { date, value: typeof rec?.distress === "number" ? rec.distress : null, episode: Boolean(rec?.isEpisode) };
      });
      return {
        id: p.user_id,
        name: p.name,
        age: p.age,
        gender: p.gender,
        level,
        activity: recentActive ? "active" : "inactive",
        trend,
        latestDistress: distressRecords.at(-1)?.distress ?? null,
        phq: latestScoreUntil(p.phqTests, end),
        gad: latestScoreUntil(p.gadTests, end),
      };
    })
    .filter(Boolean);
};

// ─── 참가자 상세 ────────────────────────────────────────────────────
/** 날짜별 기관(조회 범위) 평균 — 값이 있는 참가자만 평균, 아무도 없으면 null */
const getOrgDailyMap = (dataset, scopeKey) => {
  if (dataset.orgDailyCache.has(scopeKey)) return dataset.orgDailyCache.get(scopeKey);
  const sums = new Map();
  getScopeParticipants(dataset, scopeKey).forEach((p) => {
    p.records.forEach((r) => {
      const entry = sums.get(r.date) ?? { sym: [], emo: [], distress: [] };
      ["sym", "emo", "distress"].forEach((key) => {
        if (typeof r[key] === "number") entry[key].push(r[key]);
      });
      sums.set(r.date, entry);
    });
  });
  const map = new Map(
    [...sums.entries()].map(([date, entry]) => [
      date,
      {
        org_symptom_avg: entry.sym.length ? round2(mean(entry.sym)) : null,
        org_emo_avg: entry.emo.length ? round2(mean(entry.emo)) : null,
        org_distress_avg: entry.distress.length ? round2(mean(entry.distress)) : null,
      },
    ]),
  );
  dataset.orgDailyCache.set(scopeKey, map);
  return map;
};

const EMPTY_ORG_DAILY = { org_symptom_avg: null, org_emo_avg: null, org_distress_avg: null };

/** 기간 내 날짜별 지표 (기존 buildDailyScores와 같은 형태) */
export const buildDailyScores = (dataset, participant, scopeKey, start, end) => {
  const orgDaily = getOrgDailyMap(dataset, scopeKey);
  return getDateList(start, end).map((date) => {
    const rec = participant.daily.get(date);
    const org = orgDaily.get(date) ?? EMPTY_ORG_DAILY;
    return {
      date,
      symptom_score: rec?.sym ?? null,
      emo_score: rec?.emo ?? null,
      distress_score: rec?.distress ?? null,
      baseline_score: rec?.baseline ?? null,
      records: isActiveRecord(rec) ? { emotion_check: rec.diaries > 0, chatbot: rec.messages > 0 } : null,
      is_episode: Boolean(rec?.isEpisode),
      ...org,
    };
  });
};

/** 기간 요약 (기존 buildWeekSummary와 같은 형태) */
export const buildWeekSummary = (dataset, dailyScores, scopeKey, start, end) => {
  const avg = (key) => {
    const values = dailyScores.map((d) => d[key]).filter((v) => typeof v === "number");
    return values.length ? round2(mean(values)) : null;
  };
  const overview = buildOverviewSummary(dataset, scopeKey, start, end);

  return {
    period_start: start,
    period_end: end,
    record_days: dailyScores.filter((d) => typeof d.distress_score === "number").length,
    symptom_avg: avg("symptom_score"),
    emo_avg: avg("emo_score"),
    distress_avg: avg("distress_score"),
    org_symptom_avg: avg("org_symptom_avg"),
    org_emo_avg: avg("org_emo_avg"),
    org_distress_avg: avg("org_distress_avg"),
    episode_count: dailyScores.filter((d) => d.is_episode).length,
    // 조회 종료일 기준 최근 2일 안에 에피소드가 있으면 "진행 중"으로 봄
    recent_episode: dailyScores.slice(-2).some((d) => d.is_episode),
    org_episode_avg: overview.org_respondents
      ? Math.round((overview.episode_total / overview.org_respondents) * 10) / 10
      : 0,
    org_respondents: overview.org_respondents,
    org_total: overview.org_total,
  };
};

// ─── 기관 통계 리포트 ───────────────────────────────────────────────
/** 조회 기간 내 검사 기록 [{ date, userId, score }] */
export const buildScreeningRecords = (dataset, scopeKey, start, end, key) =>
  getScopeParticipants(dataset, scopeKey).flatMap((p) =>
    (key === "gad" ? p.gadTests : p.phqTests)
      .filter((t) => t.date >= start && t.date <= end)
      .map((t) => ({ date: t.date, userId: p.user_id, score: t.score })),
  );

/** 조회 기간의 날짜별 기관 평균 디스트레스 [{ date, org_distress_avg }] (기록이 없는 날은 null) */
export const buildOrgDistressSeries = (dataset, scopeKey, start, end) => {
  const orgDaily = getOrgDailyMap(dataset, scopeKey);
  return getDateList(start, end).map((date) => ({
    date,
    org_distress_avg: orgDaily.get(date)?.org_distress_avg ?? null,
  }));
};

// ─── Context ───────────────────────────────────────────────────────
export const WsDataContext = createContext(null);
export const useWsDataset = () => useContext(WsDataContext);
